import { useCallback } from "react";
import { useApiService } from "./apiService";
import { AppRoleDto, TenantDto } from "../types/accessManagement";

export interface AddUserRequest {
  entraObjectId?: string;
  email: string;
  displayName: string;
  roleName: string;
  tenantId?: string | null;
  isEnabled: boolean;
  startDateUtc?: string;
  endDateUtc?: string;
  disAllowFeatures?: string[];
  disAllowAirframes?: string[];
}

export interface UpdateAppUserTenantRoleMappingRequest {
  id?: string | null;
  tenantId: string | null;
  role: string;
  isDelete: boolean;
  disAllowFeatures?: string[];
  disAllowAirframes?: string[];
}

export interface UpdateAppUserRequest {
  entraObjectId: string;
  email: string;
  displayName: string;
  isEnabled: boolean;
  startDateUtc?: string;
  endDateUtc?: string;
  tenantRoleMappings: UpdateAppUserTenantRoleMappingRequest[];
}

export interface UserTenantFeatureOverridesDto {
  tenantId: string;
  disAllowFeatures: string[];
}

export interface UserTenantAirframeOverridesDto {
  tenantId: string;
  disAllowAirframes: string[];
}

type OverridesEnvelope<T> = {
  userFound: boolean;
  overrides: T[];
};

const unwrapOverrides = <T>(data: unknown): T[] => {
  if (Array.isArray(data)) {
    return data as T[];
  }

  if (data && typeof data === "object" && "overrides" in (data as any)) {
    const overrides = (data as any).overrides;
    return Array.isArray(overrides) ? (overrides as T[]) : [];
  }

  return [];
};

export interface AppUserTenantRoleMappingDto {
  id: string;
  tenantId: string;
  tenantName?: string;
  role: string;
}

export interface AppUserDto {
  id: string;
  entraObjectId: string;
  email: string;
  displayName: string;
  userType: string;
  isEnabled: boolean;
  isGlobalAdmin?: boolean;
  startDateUtc?: string;
  endDateUtc?: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  tenantRoleMappings?: AppUserTenantRoleMappingDto[];
}

export interface SyncAzureUsersResult {
  insertedCount: number;
  updatedCount: number;
  skippedCount: number;
}

export const useAccessManagementService = () => {
  const apiService = useApiService();

  const getTenants = useCallback(async (): Promise<TenantDto[]> => {
    const response = await apiService.apiCall<TenantDto[]>({
      method: "GET",
      endpoint: "/api/Tenants",
      requireAuth: true,
    });
    return response.data ?? [];
  }, [apiService]);

  const getRoles = useCallback(async (): Promise<AppRoleDto[]> => {
    const response = await apiService.apiCall<AppRoleDto[]>({
      method: "GET",
      endpoint: "/api/AppRoles",
      requireAuth: true,
    });
    return response.data ?? [];
  }, [apiService]);

  const getAllUsers = useCallback(async (): Promise<AppUserDto[]> => {
    const response = await apiService.apiCall<AppUserDto[]>({
      method: "GET",
      endpoint: "/api/AppUsers",
      requireAuth: true,
    });
    return response.data ?? [];
  }, [apiService]);

  const getTenantUsers = useCallback(async (tenantId: string): Promise<AppUserDto[]> => {
    const response = await apiService.apiCall<AppUserDto[]>({
      method: "GET",
      endpoint: `/api/AppUsers/${tenantId}`,
      requireAuth: true,
    });
    return response.data ?? [];
  }, [apiService]);

  const addUser = useCallback(
    async (request: AddUserRequest): Promise<boolean> => {
      const response = await apiService.apiCall<boolean>({
        method: "POST",
        endpoint: "/api/AppUsers",
        payload: request,
        requireAuth: true,
      });
      return response?.data === true;
    },
    [apiService]
  );

  const deleteUser = useCallback(
    async (userId: string): Promise<boolean> => {
      const response = await apiService.apiCall<boolean>({
        method: "DELETE",
        endpoint: `/api/AppUsers/${userId}`,
        requireAuth: true,
      });
      return response?.data === true;
    },
    [apiService]
  );

  const updateUser = useCallback(
    async (request: UpdateAppUserRequest): Promise<boolean> => {
      const response = await apiService.apiCall<boolean>({
        method: "PUT",
        endpoint: "/api/AppUsers",
        payload: request,
        requireAuth: true,
      });
      return response?.data === true;
    },
    [apiService]
  );

  const getUserFeatureOverrides = useCallback(
    async (entraObjectId: string): Promise<UserTenantFeatureOverridesDto[]> => {
      try {
        const response = await apiService.apiCall<
          UserTenantFeatureOverridesDto[] | OverridesEnvelope<UserTenantFeatureOverridesDto>
        >({
          method: "GET",
          endpoint: `/api/AppUsers/${entraObjectId}/feature-overrides`,
          requireAuth: true,
        });

        return unwrapOverrides<UserTenantFeatureOverridesDto>(response.data);
      } catch (error: any) {
        if (error?.status === 404) {
          return [];
        }

        throw error;
      }
    },
    [apiService]
  );

  const getUserAirframeOverrides = useCallback(
    async (entraObjectId: string): Promise<UserTenantAirframeOverridesDto[]> => {
      try {
        const response = await apiService.apiCall<
          UserTenantAirframeOverridesDto[] | OverridesEnvelope<UserTenantAirframeOverridesDto>
        >({
          method: "GET",
          endpoint: `/api/AppUsers/${entraObjectId}/airframe-overrides`,
          requireAuth: true,
        });

        return unwrapOverrides<UserTenantAirframeOverridesDto>(response.data);
      } catch (error: any) {
        if (error?.status === 404) {
          return [];
        }

        throw error;
      }
    },
    [apiService]
  );

  const syncAzureUsers = useCallback(async (): Promise<SyncAzureUsersResult> => {
    const response = await apiService.apiCall<SyncAzureUsersResult>({
      method: "POST",
      endpoint: "/api/AppUsers/syncAzureUsers",
      requireAuth: true,
    });

    return (
      response.data ?? {
        insertedCount: 0,
        updatedCount: 0,
        skippedCount: 0,
      }
    );
  }, [apiService]);

  return {
    getTenants,
    getRoles,
    getAllUsers,
    getTenantUsers,
    addUser,
    deleteUser,
    updateUser,
    getUserFeatureOverrides,
    getUserAirframeOverrides,
    syncAzureUsers,
  };
};
